import React, { useState, useEffect } from 'react';
import { Select, InputNumber, Checkbox, TimePicker, DatePicker } from 'antd';
import dayjs from 'dayjs';

export default function RecurrenceBuilder({ onChange, initialData }){
  const loadLocalFromUTC = (utcDateStr, utcTimeStr) => {
    if (!utcTimeStr) return null;
    const datePart = utcDateStr ? utcDateStr.split('T')[0] : dayjs().format('YYYY-MM-DD');
    return dayjs.utc(`${datePart}T${utcTimeStr}`).tz(dayjs.tz.guess());
  };

  const getInitialLocalTime = (utcTimeStr) => {
    if (!utcTimeStr) return null;
    return loadLocalFromUTC(initialData?.first_recurrence, utcTimeStr).format('HH:mm');
  };

  const getInitialLocalDate = (utcDateStr, utcTimeStr) => {
    if (!utcDateStr) return '';
    if (!utcTimeStr) return utcDateStr.split('T')[0];
    return loadLocalFromUTC(utcDateStr, utcTimeStr).format('YYYY-MM-DD');
  };

  // Determine initial month mode from initialData
  const getInitialMonthMode = () => {
    if (!initialData) return 'day_of_month';
    if (initialData.last_of_month) return 'last_of_month';
    if (initialData.week_of_month != null && initialData.days_of_week) return 'week_of_month';
    return 'day_of_month';
  };

  const [frequency, setFrequency] = useState(initialData?.frequency || 'WEEK');
  const [interval, setInterval] = useState(initialData?.interval || 1);
  const [days, setDays] = useState(initialData?.days_of_week ? initialData.days_of_week.split(',').map(Number) : []);
  const [startTime, setStartTime] = useState(initialData?.start_time ? getInitialLocalTime(initialData.start_time) : '09:00');
  const [endTime, setEndTime] = useState(initialData?.end_time ? getInitialLocalTime(initialData.end_time) : '10:00');
  const [firstRecurrence, setFirstRecurrence] = useState(initialData?.first_recurrence ? getInitialLocalDate(initialData.first_recurrence, initialData.start_time) : '');
  const [endRecurrency, setEndRecurrency] = useState(initialData?.end_recurrency ? getInitialLocalDate(initialData.end_recurrency, initialData.start_time) : '');

  // Monthly-specific state
  const [monthMode, setMonthMode] = useState(getInitialMonthMode());
  const [dayOfMonth, setDayOfMonth] = useState(initialData?.day_of_month || null);
  const [weekOfMonth, setWeekOfMonth] = useState(initialData?.week_of_month || null);
  const [lastOfMonth, setLastOfMonth] = useState(initialData?.last_of_month ? true : false);

  // Yearly-specific state
  const [monthOfYear, setMonthOfYear] = useState(initialData?.month_of_year || null);

  useEffect(() => {
    if (initialData) {
      if (initialData.frequency) setFrequency(initialData.frequency);
      if (initialData.interval) setInterval(initialData.interval);
      if (initialData.days_of_week) setDays(initialData.days_of_week.split(',').map(Number));
      if (initialData.start_time) setStartTime(getInitialLocalTime(initialData.start_time));
      if (initialData.end_time) setEndTime(getInitialLocalTime(initialData.end_time));
      if (initialData.first_recurrence) setFirstRecurrence(getInitialLocalDate(initialData.first_recurrence, initialData.start_time));
      if (initialData.end_recurrency) setEndRecurrency(getInitialLocalDate(initialData.end_recurrency, initialData.start_time));

      // Monthly fields
      if (initialData.day_of_month != null) setDayOfMonth(initialData.day_of_month);
      if (initialData.week_of_month != null) setWeekOfMonth(initialData.week_of_month);
      if (initialData.last_of_month) setLastOfMonth(true);
      setMonthMode(getInitialMonthMode());

      // Yearly fields
      if (initialData.month_of_year != null) setMonthOfYear(initialData.month_of_year);
    }
  }, [initialData]);

  // Build and emit payload whenever relevant state changes
  useEffect(()=>{
    const payload = {
      frequency,
      interval,
      start_time: startTime,
      end_time: endTime,
      first_recurrence: firstRecurrence,
      end_recurrency: endRecurrency,
      // Reset all optional fields — set below per frequency
      days_of_week: null,
      day_of_month: null,
      week_of_month: null,
      month_of_year: null,
      last_of_month: null,
    };

    if (frequency === 'WEEK') {
      payload.days_of_week = days.length ? days.join(',') : null;
    }

    if (frequency === 'MONTH') {
      if (monthMode === 'day_of_month') {
        payload.day_of_month = dayOfMonth;
      } else if (monthMode === 'week_of_month') {
        payload.week_of_month = weekOfMonth;
        payload.days_of_week = days.length ? days.join(',') : null;
      } else if (monthMode === 'last_of_month') {
        payload.last_of_month = true;
      }
    }

    if (frequency === 'YEAR') {
      payload.month_of_year = monthOfYear;
      payload.day_of_month = dayOfMonth;
    }

    if(onChange) onChange(payload);
  }, [frequency, interval, days, startTime, endTime, firstRecurrence, endRecurrency, monthMode, dayOfMonth, weekOfMonth, lastOfMonth, monthOfYear]);

  // Reset irrelevant fields when frequency changes
  const handleFrequencyChange = (val) => {
    setFrequency(val);
    // Clear fields that don't apply to the new frequency
    if (val !== 'WEEK' && val !== 'MONTH') {
      setDays([]);
    }
    if (val !== 'MONTH') {
      setMonthMode('day_of_month');
      setDayOfMonth(null);
      setWeekOfMonth(null);
      setLastOfMonth(false);
    }
    if (val !== 'YEAR') {
      setMonthOfYear(null);
    }
    if (val === 'MONTH') {
      setDays([]);
    }
    if (val === 'YEAR') {
      setDays([]);
      setDayOfMonth(null);
    }
  };

  const dayOptions = [
    { label: 'Lun', value: 1 },
    { label: 'Mar', value: 2 },
    { label: 'Mie', value: 3 },
    { label: 'Jue', value: 4 },
    { label: 'Vie', value: 5 },
    { label: 'Sab', value: 6 },
    { label: 'Dom', value: 0 },
  ];

  const weekOfMonthOptions = [
    { value: 1, label: 'Primera' },
    { value: 2, label: 'Segunda' },
    { value: 3, label: 'Tercera' },
    { value: 4, label: 'Cuarta' },
    { value: 5, label: 'Última' },
  ];

  const dayOfWeekSelectOptions = [
    { value: 1, label: 'Lunes' },
    { value: 2, label: 'Martes' },
    { value: 3, label: 'Miércoles' },
    { value: 4, label: 'Jueves' },
    { value: 5, label: 'Viernes' },
    { value: 6, label: 'Sábado' },
    { value: 0, label: 'Domingo' },
  ];

  const monthOptions = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
  ];

  const frequencyLabel = {
    DAY: 'días',
    WEEK: 'semanas',
    MONTH: 'meses',
    YEAR: 'años',
  };

  return (
    <div>
      <h4>Recurrencia</h4>

      {/* Frequency */}
      <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
        <label>Frecuencia:</label>
        <Select value={frequency} onChange={handleFrequencyChange} className="w-100" options={[
          { value: 'DAY', label: 'Diario' },
          { value: 'WEEK', label: 'Semanal' },
          { value: 'MONTH', label: 'Mensual' },
          { value: 'YEAR', label: 'Anual' }
        ]} />
      </div>

      {/* Interval */}
      <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
        <label>Cada cuántos {frequencyLabel[frequency] || 'períodos'}:</label>
        <InputNumber min={1} value={interval} onChange={val=>setInterval(val||1)} className="w-100" />
      </div>

      {/* WEEK: Days of week */}
      {frequency === 'WEEK' && (
        <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
          <label>Días de la semana:</label>
          <Checkbox.Group options={dayOptions} value={days} onChange={(vals)=>setDays(vals)} />
          {frequency === 'WEEK' && days.length === 0 && (
            <div style={{ color: '#ff4d4f', marginTop: 6 }}>Selecciona al menos un día de la semana.</div>
          )}
        </div>
      )}

      {/* MONTH: Mode selector + fields */}
      {frequency === 'MONTH' && (
        <>
          <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
            <label>Repetir el:</label>
            <Select value={monthMode} onChange={(val) => {
              setMonthMode(val);
              // Reset month-specific fields on mode change
              setDayOfMonth(null);
              setWeekOfMonth(null);
              setDays([]);
              setLastOfMonth(val === 'last_of_month');
            }} className="w-100" options={[
              { value: 'day_of_month', label: 'Día fijo del mes (ej. día 15)' },
              { value: 'week_of_month', label: 'Día de la semana específico (ej. 2do Martes)' },
              { value: 'last_of_month', label: 'Último día del mes' },
            ]} />
          </div>

          {/* MONTH - Day of month */}
          {monthMode === 'day_of_month' && (
            <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
              <label>Día del mes:</label>
              <InputNumber min={1} max={31} value={dayOfMonth} onChange={v => setDayOfMonth(v)} className="w-100" placeholder="Ej. 15" />
            </div>
          )}

          {/* MONTH - Nth weekday */}
          {monthMode === 'week_of_month' && (
            <>
              <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
                <label>¿Cuál semana del mes?</label>
                <Select value={weekOfMonth} onChange={val => setWeekOfMonth(val)} className="w-100" placeholder="Selecciona semana" options={weekOfMonthOptions} />
              </div>
              <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
                <label>¿Qué día de la semana?</label>
                <Select value={days.length ? days[0] : undefined} onChange={val => setDays([val])} className="w-100" placeholder="Selecciona día" options={dayOfWeekSelectOptions} />
                {monthMode === 'week_of_month' && weekOfMonth && days.length === 0 && (
                  <div style={{ color: '#ff4d4f', marginTop: 6 }}>Selecciona un día de la semana para esta recurrencia mensual.</div>
                )}
              </div>
            </>
          )}

          {/* MONTH - Last of month: no extra fields needed */}
        </>
      )}

      {/* YEAR: month + day selectors */}
      {frequency === 'YEAR' && (
        <>
          <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
            <label>Mes del año:</label>
            <Select value={monthOfYear} onChange={val => setMonthOfYear(val)} className="w-100" placeholder="Selecciona mes" options={monthOptions} />
          </div>
          <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
            <label>Día del mes:</label>
            <InputNumber min={1} max={31} value={dayOfMonth} onChange={v => setDayOfMonth(v)} className="w-100" placeholder="Ej. 25" />
          </div>
        </>
      )}

      {/* Start time */}
      <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
        <label>Hora inicio:</label>
        <TimePicker value={startTime ? dayjs(startTime, 'HH:mm') : undefined} format={'HH:mm'} onChange={(t)=>setStartTime(t ? dayjs(t).format('HH:mm') : '')} className="w-100" needConfirm={false}/>
      </div>

      {/* End time */}
      <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
        <label>Hora fin:</label>
        <TimePicker value={endTime ? dayjs(endTime, 'HH:mm') : undefined} format={'HH:mm'} onChange={(t)=>setEndTime(t ? dayjs(t).format('HH:mm') : '')} className="w-100" needConfirm={false}/>
      </div>

      {/* First recurrence */}
      <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
        <label>Primera ocurrencia (fecha):</label>
        <DatePicker picker="date" value={firstRecurrence ? dayjs(firstRecurrence) : undefined} onChange={(d)=>setFirstRecurrence(d ? dayjs(d).format('YYYY-MM-DD') : '')} className="w-100" disabledDate={(current) => current && current < dayjs().startOf('day')} />
      </div>

      {/* End recurrence */}
      <div className="input-group col-10 col-md-5 p-0 mx-auto mt-3">
        <label>Fin de recurrencia (opcional, fecha):</label>
        <DatePicker picker="date" value={endRecurrency ? dayjs(endRecurrency) : undefined} onChange={(d)=>setEndRecurrency(d ? dayjs(d).format('YYYY-MM-DD') : '')} className="w-100" disabledDate={(current) => {
          if (!firstRecurrence) return current && current < dayjs().startOf('day');
          return current && current < dayjs(firstRecurrence).startOf('day');
        }} />
      </div>
    </div>
  )
}
